import React, { useState } from 'react';
import { Divider, IconButton, Popover, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import type { SxProps } from '@mui/system/styleFunctionSx';

export interface PopoverMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  sx?: SxProps;
  /**
   * Drop the item entirely instead of greying it out. BOPS-151/BOPS-173/BOPS-174 require
   * Archive to be absent — not disabled — when the toggle is off, the permission is missing,
   * the note has no PDF or the month is closed. `disabled` cannot express that.
   */
  hidden?: boolean;
  /** Render a divider above this item, separating the irreversible actions from the rest. */
  dividerBefore?: boolean;
  /** Automation handle (BOPS-262). Omitted items carry no attribute. */
  testId?: string;
}

interface PopoverMenuProps {
  items: PopoverMenuItem[];
  /** Automation handle for the trigger that opens this menu. */
  testId?: string;
}

const PopoverMenu: React.FC<PopoverMenuProps> = ({ items, testId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        data-testid={testId}
        onClick={handleClick}
        size="small"
      >
        <MoreVert />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items
          .filter((item) => !item.hidden)
          .map((item, idx, visibleItems) => [
            //  One rule per group, not per item: a run of adjacent dividerBefore items (the
            //  irreversible pair, Archive and Delete) gets a single separator, and if the
            //  first of them is hidden the next one inherits it. Skipped at idx 0 so a menu
            //  never opens with a stray line at the top.
            item.dividerBefore && idx > 0 && !visibleItems[idx - 1]?.dividerBefore ? (
              <Divider
                key={`${item.label + idx}-divider`}
                sx={{ my: 0.5 }}
              />
            ) : null,
            <MenuItem
              key={item.label + idx}
              data-testid={item.testId}
              onClick={() => {
                handleClose();
                item.onClick();
              }}
              disabled={item.disabled}
              sx={item.sx}
            >
              {item.icon && <ListItemIcon sx={{ marginRight: '0px' }}>{item.icon}</ListItemIcon>}
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>,
          ])}
      </Popover>
    </>
  );
};

export default PopoverMenu;
